import { useState } from 'react';

import {
  useLazyListBuildingsQuery,
  useUpsertAssetMutation,
  useGetOneAssetQuery, // this one is the for initial load, which happens before the ui renders anything
  useLazyGetOneAssetQuery,
  useBulkUpdateAssetsMutation,
  useLazyListNonContainersQuery,
  useLazyListAssetTypesQuery,
  useDeleteAssetMutation
} from '../../apiSlice/assetApi';

import useSearchCallback from '../../apiSlice/useSearchCallback';
import useFetchAuxiliaryLists from '../../apiSlice/useFetchAuxiliaryLists';
import { BucketConfig } from '../../../sharedcomponents/5templates/detailCards/BucketConfig';
import {
  ArtificialDetailPropertyNames,
  RootObjectTypes
} from '../../../common/globalNotary';
import { useBulkUpdateContractsMutation } from '../../apiSlice/contractApi';
import { getPushPull } from '../../apiSlice/pushPullHelper';
import { useNavigate } from 'react-router-dom';

const useLocation = () => {
  const [loadLocations] = useLazyListNonContainersQuery();
  const [loadBuildings] = useLazyListBuildingsQuery();
  const [getOneAsset] = useLazyGetOneAssetQuery();
  const [bulkUpdateLocationsCallBack] = useBulkUpdateAssetsMutation();
  const [upsertLocationRecordCallback] = useUpsertAssetMutation();
  const [bulkUpdateContracts] = useBulkUpdateContractsMutation();
  const [loadAssetTypes] = useLazyListAssetTypesQuery();
  const [deleteAsset] = useDeleteAssetMutation();
  const navigate = useNavigate();

  const {
    searchContractsCallback,
    searchServicesCallback,
    searchPartiesCallback,
    searchNonContainerAssetsCallback
  } = useSearchCallback();

  const [finalAuxiliaryRecords, setFinalAuxiliaryRecords] = useState('');
  const [loading, setLoading] = useState(false);

  const constructAndSetFinalListBucket = (
    completeResponse,
    primaryLocationRecord
  ) => {
    const finalListBuckets = {};

    finalListBuckets.assetTypes = completeResponse[3].data;
    finalListBuckets.entities = completeResponse[4].data;
    finalListBuckets.buildings = completeResponse[5].data;

    finalListBuckets.primero = [];

    if (primaryLocationRecord.isBuilding) {
      const multiTenantTypeId = finalListBuckets.assetTypes.find(type => type.base_type === 'multi')?.id;
      finalListBuckets.segundo = [
        // FIXME: What a hell of passing args? Refactor to options
        new BucketConfig(
          completeResponse[6].data,
          keyword => multiTenantTypeId
            ? searchNonContainerAssetsCallback(keyword, null, multiTenantTypeId)
            : Promise.resolve([]),
          'Locations',
          null,
          undefined,
          undefined,
          undefined,
          true // isAddOnly
        )
      ];
    } else {
      finalListBuckets.segundo = [
        new BucketConfig(
          completeResponse[0].data,
          searchContractsCallback,
          'Accounts',
          null
        )
      ];
      finalListBuckets.tercero = [
        new BucketConfig(
          completeResponse[1].data,
          searchPartiesCallback,
          'Contacts',
          null
        ),
        new BucketConfig(
          completeResponse[2].data,
          searchServicesCallback,
          'Services',
          null
        )
      ];
    }

    setFinalAuxiliaryRecords(finalListBuckets);
  };

  const {
    constructServicesByIdArrayCall,
    constructPartiesByIdArrayCall,
    constructGetAllAssetTypesCall,
    constructGetAllEntitiesCall,
    constructGetAllBuildingsCall,
    constructValueAsPromiseCall
  } = useFetchAuxiliaryLists();

  const fetchAuxiliaryRecords = async (primaryLocationRecord) => {
    const fetchCallArray = [
      // Contracts
      constructValueAsPromiseCall(
        ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4,
        primaryLocationRecord[
          ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4
        ]
      ),
      constructPartiesByIdArrayCall(primaryLocationRecord, 'party_ids'),
      constructServicesByIdArrayCall(primaryLocationRecord, 'service_ids'),
      constructGetAllAssetTypesCall(
        primaryLocationRecord,
        ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1
      ),

      constructGetAllEntitiesCall(
        primaryLocationRecord,
        ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2
      ),
      constructGetAllBuildingsCall(),
      constructValueAsPromiseCall(
        ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3,
        primaryLocationRecord[
          ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3
        ]
      )
    ];

    /// this Promise.all will not reject, errors come back on the error object,
    /// if everything works out, the error object is '' or undefined
    Promise.all(fetchCallArray.map((f) => f.call()))
      .then((res) => {
        constructAndSetFinalListBucket(res, primaryLocationRecord);
      })
      .catch(console.error);
  };

  const removeLocation = async (id) => {
    deleteAsset({ id })
      .then(() => {
        navigate(-1);
      })
      .catch((err) => {
        console.error(err);
        alert(
          'Could not delete asset. Please check that other objects do not depend on it.'
        );
      });
  };

  const suspendAsset = async (id, active) => {
    bulkUpdateLocationsCallBack([
      { id, suspended_at: active ? null : new Date() }
    ]);
  };

  const upsertLocationCallback = (primaryRecord) => {
    const returnPromise = new Promise((resolve, reject) => {
      const isBuilding = primaryRecord.isBuilding;

      setLoading(true);
      upsertLocationRecordCallback(primaryRecord)
        .unwrap()
        .then((primaryResult) => {
          const id = primaryResult.data.id
            ? primaryResult.data.id
            : primaryResult.data.data.id;

          const toSave = [];

          if (isBuilding) {
            const containeeUpdateArray = primaryRecord[
              ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3
            ].map((o) => {
              return { id: o.id, parent_id: id };
            });
            if (containeeUpdateArray.length) {
              toSave.push(bulkUpdateLocationsCallBack(containeeUpdateArray));
            }
          } else {
            const contractPushPull = getPushPull(
              primaryRecord,
              ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4,
              ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4_INITIAL_VALUES
            );
            const contractPatch = [];
            contractPatch.push(
              ...contractPushPull.push?.map((contractId) => ({
                id: contractId,
                asset_id_assign: id
              })),
              ...contractPushPull.pull?.map((contractId) => ({
                id: contractId,
                asset_id_unassign: id
              }))
            );

            if (contractPatch.length) {
              toSave.push(bulkUpdateContracts(contractPatch));
            }
          }

          Promise.all(toSave)
            .then((results) => {
              const errors = results?.flatMap((r) => r.data?.errors ?? []);
              errors.length === 0
                ? resolve({ id, type: RootObjectTypes.ASSET })
                : reject(
                    'The following errors have occurred: ' +
                      errors.map((e) => e.msg).join(', ')
                  );
            })
            .catch(reject);
        })
        .catch(reject)
        .finally(() => {
          setLoading(false);
        });
    });

    return returnPromise;
  };

  const listAssetTypes = async () => {
    const { data: { data: assetTypes } = { data: [] } } =
      await loadAssetTypes();

    return assetTypes;
  };

  /*const typeIdToCodeName = typeId => {
    const { data: { data: types } = { data: [] } } = listAssetTypes
    return types?.find(t => t.id === typeId)?.base_type ?? ''
  }*/

  const assetTypes = [
    'multi',
    'single',
    'building',
    'satellite',
    'mobile',
    'site'
  ];

  const makeInMemoryLocationRecord = (type) => {
    const returnValue = {
      name: '',
      description: '',
      type_id: '',
      phone: '',
      parent_id: '',
      floor_unit: '',
      building_floor: '',
      num_floors_units: '',
      year_built: '',
      strata: '',
      geo: { wkt: null, lat: 0, long: 0, radius: 0 },
      address: {},
      party_ids: [],
      service_ids: [],
      is_hidden: false,
      active: true,
      config: {},
      isBuilding: type === 'building',
      _objectId: '',
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4]: [],
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY4_INITIAL_VALUES]: []
    };

    if (type === 'building') {
      returnValue[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3] = [];
      returnValue[
        ArtificialDetailPropertyNames.DETACHED_DEPENDENCY3_INITIAL_VALUES
      ] = [];
    }

    return returnValue;
  };

  const entityDisplayNameFromParams = (type) => {
    switch (type) {
      case 'multi':
        return 'Multi-Tenant Location';
      case 'single':
        return 'Single Tenant Location';
      case 'building':
        return 'Multi-Tenant Building';
      case 'satellite':
        return 'Satellite Location';
      case 'mobile':
        return 'Mobile Location';
      case 'site':
        return 'Site Location';
      default:
        return '';
      //throw new Error('invalid location type')
    }
  };

  const fetchBuildingAddress = async (id) => {
    if (!id) return '';
    const buildingRecord = await getOneAsset({ id });

    return buildingRecord.data.data.address;
  };
  return {
    loadLocations,
    loadBuildings,
    listAssetTypes,
    assetTypes: assetTypes.filter((t) => {
      return t !== 'building' && t !== 'site'; //buildings have a separate entry on the menu
    }),
    entityDisplayNameFromParams,
    bulkUpdateLocationsCallBack,
    useGetOneAssetQuery,
    fetchAuxiliaryRecords,
    makeInMemoryLocationRecord,
    finalAuxiliaryRecords,
    upsertLocationCallback,
    fetchBuildingAddress,
    removeLocation,
    suspendAsset,
    loading
  };
};

export default useLocation;
